import React, { useState } from 'react';
import { BuildingIcon } from 'lucide-react';
import { PageHeader } from '../../components/ui/PageHeader';
import { Panel } from '../../components/ui/Panel';
import { Badge } from '../../components/ui/Badge';
import { Meter } from '../../components/ui/Meter';
import { Tabs } from '../../components/ui/Tabs';
import { DataTable } from '../../components/ui/DataTable';
import { FeatureGate } from '../../components/platform/FeatureStatusBadge';
import { maintenanceRequests, managedProperties, occupancyTrend, rentLedger, tenancyApplications, tenants } from '../../data/property';
import { naira, percent } from '../../utils/format';
import { useSectionRoute } from '../../hooks/useSectionRoute';
const TABS = ['Portfolio', 'Inventory', 'Applications', 'Tenants', 'Rent', 'Maintenance'] as const;
const BED_TONE: Record<string, string> = {
  occupied: 'bg-brand-700',
  vacant: 'bg-line-strong',
  reserved: 'bg-warn-700',
  maintenance: 'bg-danger-700'
};
export function PropertyOverview() {
  const [tab, setTab] = useSectionRoute('/property', TABS);
  const allBeds = managedProperties.flatMap(property => property.buildings.flatMap(building => building.rooms.flatMap(room => room.beds)));
  const occupied = allBeds.filter(bed => bed.status === 'occupied').length;
  const arrears = tenants.reduce((total, tenant) => total + tenant.balance, 0);
  return <div className="space-y-6" data-id="element-9510" data-mp="3vuura978vhu1">
      <PageHeader eyebrow="Cedarwood Living" title="{property} console" description="Occupancy, applications, tenants, rent records and maintenance across your three Lagos properties." data-id="element-9511" data-mp="3vtjoocpjcg4i" />

      <Tabs tabs={TABS} value={tab} onChange={setTab} label="{property} sections" data-id="element-9512" data-mp="9nwgl7sbe6a1" />

      {tab === 'Portfolio' && <div className="grid gap-6 lg:grid-cols-3" data-id="element-9513" data-mp="dmdp9vrdvotk">
          <div className="space-y-6 lg:col-span-2" data-id="element-9514" data-mp="2bfj9xfzoid9o">
            <Panel title="{property:plural}" bodyClassName="p-0" data-id="element-9515" data-mp="2fg3a6sfjtx01">
              <ul className="divide-y divide-line" data-id="element-9516" data-mp="2fgd0yi8f486s">
                {managedProperties.map(property => {
              const beds = property.buildings.flatMap(building => building.rooms.flatMap(room => room.beds));
              const let_ = beds.filter(bed => bed.status === 'occupied').length;
              const vacant = beds.length - let_;
              return <li key={property.id} className="gap-4 p-4 sm:flex" data-id="element-9517" data-mp="5kgyew6vceig">
                      {/* A landlord recognises the building before the name. */}
                      <img src={property.image} alt={`${property.name}, ${property.address}`} loading="lazy" className="h-36 w-full shrink-0 rounded-xl object-cover sm:h-28 sm:w-44" data-id="element-9518" data-mp="kumcq2s14b8" />
                      <div className="mt-3 min-w-0 flex-1 sm:mt-0" data-id="element-9519" data-mp="obhv1dkinxyu">
                        <div className="flex flex-wrap items-start justify-between gap-3" data-id="element-9520" data-mp="jcn57ro4t958">
                          <div className="min-w-0" data-id="element-9521" data-mp="12x5fkzumt4i8">
                            <p className="text-body font-semibold text-ink" data-id="element-9522" data-mp="12vkmzfrvnvn0">{property.name}</p>
                            <p className="mt-0.5 text-body-sm text-ink-muted" data-id="element-9523" data-mp="17vyumwpkh3ki">{property.address}</p>
                            <p className="cz-num mt-0.5 text-caption text-ink-subtle" data-id="element-9524" data-mp="twpe1vxg87g0">
                              {property.kind} · {property.buildings.length} building(s) ·{' '}
                              {beds.length} beds
                            </p>
                          </div>
                          <Badge tone={property.verification === 'verified' ? 'ok' : 'warn'} data-id="element-9525" data-mp="oz77gc6wm4b6">
                            {property.verification === 'verified' ? 'Verified' : 'Verification pending'}
                          </Badge>
                        </div>
                        <div className="mt-3 w-full max-w-xs" data-id="element-9526" data-mp="1anzjxrrntjm">
                          <Meter value={percent(let_, beds.length)} label="Occupancy" trailing={`${let_}/${beds.length}`} data-id="element-9527" data-mp="66v6ntvwfb35" />
                        </div>
                        <p className="mt-2 text-caption text-ink-muted" data-id="element-9528" data-mp="3klrong2kzkpf">
                          {vacant === 0 ? 'Fully let — nothing to advertise here.' : `${vacant} ${vacant === 1 ? 'bed is' : 'beds are'} earning nothing today.`}
                        </p>
                      </div>
                    </li>;
            })}
              </ul>
            </Panel>

            <Panel title="Occupancy trend" description="Share of beds let, last six months" data-id="element-9529" data-mp="31np87po3mw38">
              <div className="flex items-end gap-3" data-id="element-9530" data-mp="2xnesvmef2tbp">
                {occupancyTrend.map(point => <div key={point.month} className="flex flex-1 flex-col items-center gap-2" data-id="element-9531" data-mp="zv9fj1k3v1vh">
                    <div className="w-full rounded-t bg-brand-700" style={{
                height: `${point.occupancy * 1.4}px`
              }} data-id="element-9532" data-mp="vuyxgqj1gl09" />
                    <span className="cz-num text-[12px] text-ink-muted" data-id="element-9533" data-mp="2ipvsfae51d3u">{point.month}</span>
                    <span className="cz-num text-[12px] font-medium text-ink" data-id="element-9534" data-mp="2niybupp7b66b">
                      {point.occupancy}%
                    </span>
                  </div>)}
              </div>
            </Panel>
          </div>

          <div className="space-y-6" data-id="element-9535" data-mp="1uvay9umclft6">
            <Panel title="Right now" bodyClassName="p-0" data-id="element-9536" data-mp="1zrsxyg2tmnyn">
              <dl className="divide-y divide-line" data-id="element-9537" data-mp="1zuf6ahxjd7hx">
                {[{
              label: 'Beds let',
              value: `${occupied} of ${allBeds.length}`
            }, {
              label: 'Vacancies',
              value: String(allBeds.length - occupied)
            }, {
              label: 'New applications',
              value: String(tenancyApplications.filter(a => a.status === 'New').length)
            }, {
              label: 'Rent in arrears',
              value: naira(arrears)
            }, {
              label: 'Open maintenance',
              value: String(maintenanceRequests.filter(request => request.status !== 'Closed').length)
            }].map(row => <div key={row.label} className="flex justify-between gap-3 px-5 py-3" data-id="element-9538" data-mp="1p5j2yq66r539">
                    <dt className="text-[13px] text-ink-muted" data-id="element-9539" data-mp="11g6wvz65nd56">{row.label}</dt>
                    <dd className="cz-num text-[13px] font-semibold text-ink" data-id="element-9540" data-mp="wh3qaaaj0yv3">{row.value}</dd>
                  </div>)}
              </dl>
            </Panel>
            <Panel title="Payments" data-id="element-9541" data-mp="16ei6c8sjflmv">
              <p className="text-[13px] leading-6 text-ink-muted" data-id="element-9542" data-mp="16dgqzs4hoczc">
                Rent is collected through HavaPay. Campuzon holds the tenancy and payment record;
                money movement stays with the regulated provider.
              </p>
            </Panel>
          </div>
        </div>}

      {tab === 'Inventory' && <div className="space-y-6" data-id="element-9543" data-mp="2pb3cjsydz2ol">
          {managedProperties.map(property => <Panel key={property.id} title={property.name} description="{property} → building → floor → room → bed" bodyClassName="p-0" data-id="element-9544" data-mp="2u57kaefh7eqq">
              {property.buildings.map(building => <div key={building.id} className="border-b border-line px-5 py-4 last:border-b-0" data-id="element-9545" data-mp="383fjyu3wcu5l">
                  <p className="flex items-center gap-2 text-[13px] font-semibold text-ink" data-id="element-9546" data-mp="3d30jp6qw56jd">
                    <BuildingIcon className="h-3.5 w-3.5 text-ink-subtle" data-id="element-9547" data-mp="2z3sik8b63ocj" />
                    {building.name}
                  </p>
                  <ul className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-id="element-9548" data-mp="340j255m6l22p">
                    {building.rooms.map(room => <li key={room.id} className="rounded-lg border border-line px-3 py-2.5" data-id="element-9549" data-mp="1smxczkgbn0h6">
                        <p className="cz-num text-[13px] font-medium text-ink" data-id="element-9550" data-mp="2jd0ld8jb2pgb">{room.label}</p>
                        <p className="text-[12px] text-ink-muted" data-id="element-9551" data-mp="2janxkeqv9kvc">
                          {room.floor} floor · {room.roomType}
                        </p>
                        <ul className="mt-2 space-y-1.5" data-id="element-9552" data-mp="2ad3ssvtsp6fz">
                          {room.beds.map(bed => <li key={bed.id} className="flex items-center gap-2 text-[12px]" data-id="element-9553" data-mp="25f28y8ojp33g">
                              <span aria-hidden="true" className={`h-2 w-2 rounded-full ${BED_TONE[bed.status]}`} data-id="element-9554" data-mp="330ggxafltmbe" />
                              <span className="text-ink-muted" data-id="element-9555" data-mp="2t9bx9xezhda8">{bed.label}</span>
                              <span className="ml-auto text-ink-subtle" data-id="element-9556" data-mp="2obk9nfldgdbu">
                                {bed.tenantName ?? bed.status}
                              </span>
                            </li>)}
                        </ul>
                      </li>)}
                  </ul>
                </div>)}
            </Panel>)}
        </div>}

      {tab === 'Applications' && <Panel title="Applications" bodyClassName="p-0" data-id="element-9557" data-mp="24z0owr3qbzb">
          <DataTable caption="Tenancy applications awaiting a decision" rowKey={application => application.id} rows={tenancyApplications} columns={[{
        header: 'Applicant',
        primary: true,
        cell: a => a.applicant
      }, {
        header: 'Programme',
        cell: a => a.programme
      }, {
        header: 'Property',
        cell: a => a.property
      }, {
        header: 'Room',
        cell: a => a.roomPreference
      }, {
        header: 'Move-in',
        cell: a => <span className="cz-num" data-id="element-9559" data-mp="utmksp046vba">{a.moveIn}</span>
      }, {
        header: 'Status',
        status: true,
        cell: a => <Badge tone={a.status === 'Approved' ? 'ok' : a.status === 'Declined' ? 'danger' : a.status === 'Screening' ? 'brand' : 'warn'} data-id="element-9560" data-mp="36yb2rj159ukz">
                    {a.status}
                  </Badge>
      }]} data-id="element-9558" data-mp="23e30y9hqcx8" />
        </Panel>}

      {tab === 'Tenants' && <Panel title="Tenants" bodyClassName="p-0" data-id="element-9561" data-mp="3umaw8qpubz39">
          <DataTable caption="Current tenants and their standing" rowKey={tenant => tenant.id} rows={tenants} columns={[{
        header: 'Tenant',
        primary: true,
        cell: t => t.name
      }, {
        header: 'Unit',
        cell: t => <span className="cz-num" data-id="element-9563" data-mp="2a93lin7k67mv">
                    {t.property} · {t.unit}
                  </span>
      }, {
        header: 'Lease',
        cell: t => <span className="cz-num" data-id="element-9564" data-mp="1xwhu52ovp0e1">
                    {t.leaseStart} — {t.leaseEnd}
                  </span>
      }, {
        header: 'Rent',
        align: 'right',
        cell: t => <span className="cz-num" data-id="element-9565" data-mp="2lnx1xazfc9wo">{naira(t.rentPerYear)}</span>
      }, {
        header: 'Balance',
        align: 'right',
        cell: t => <span className="cz-num font-medium" data-id="element-9566" data-mp="2boelv5mmx89w">
                    {t.balance === 0 ? '—' : naira(t.balance)}
                  </span>
      }, {
        header: 'Status',
        status: true,
        cell: t => <Badge tone={t.status === 'Overdue' ? 'danger' : t.status === 'Ending soon' ? 'warn' : 'ok'} data-id="element-9567" data-mp="vag3c2jqv6my">
                    {t.status}
                  </Badge>
      }]} data-id="element-9562" data-mp="3hude14pwccb" />
        </Panel>}

      {tab === 'Rent' && <div className="space-y-5" data-id="element-9568" data-mp="39kteyoj4iuth">
          <FeatureGate featureKey="rent_payment" data-id="element-9569" data-mp="3ehad8bk1epka" />
        <Panel title="Rent ledger" description="A record of what is owed and what has been received. Campuzon does not collect it." bodyClassName="p-0" data-id="element-9570" data-mp="3efyy5fl55vdv">
          <ul className="divide-y divide-line" data-id="element-9571" data-mp="34l6ym6800ygg">
            {rentLedger.map(record => <li key={record.id} className="flex flex-wrap items-center gap-3 px-5 py-3.5" data-id="element-9572" data-mp="2qtiscjkfl47e">
                <div className="min-w-0 flex-1" data-id="element-9573" data-mp="2vs3skizonnvu">
                  <p className="text-[13px] font-medium text-ink" data-id="element-9574" data-mp="2gu1kbvhpie89">{record.tenant}</p>
                  <p className="cz-num mt-0.5 text-[12px] text-ink-muted" data-id="element-9575" data-mp="2gwe0c33wmeio">
                    {record.unit} · {record.period} · due {record.due} · {record.method}
                  </p>
                </div>
                <span className="cz-num text-[13px] font-semibold text-ink" data-id="element-9576" data-mp="2295ttfljixd1">
                  {naira(record.amount)}
                </span>
                <Badge tone={record.status === 'Paid' ? 'ok' : record.status === 'Overdue' ? 'danger' : 'warn'} data-id="element-9577" data-mp="2c6k04mdr25rm">
                  {record.status}
                </Badge>
              </li>)}
          </ul>
        </Panel>
        </div>}

      {tab === 'Maintenance' && <Panel title="Maintenance requests" bodyClassName="p-0" data-id="element-9578" data-mp="2pwx82oqn5kgy">
          <ul className="divide-y divide-line" data-id="element-9579" data-mp="2pxgqs7o48cw1">
            {maintenanceRequests.map(request => <li key={request.id} className="flex flex-wrap items-center gap-3 px-5 py-3.5" data-id="element-9580" data-mp="34ufuvljrdnlr">
                <div className="min-w-0 flex-1" data-id="element-9581" data-mp="2zwo4wx2ocbk7">
                  <p className="cz-num text-[13px] font-medium text-ink" data-id="element-9582" data-mp="x8plyuxifg65">
                    {request.reference} · {request.category}
                  </p>
                  <p className="mt-0.5 text-[12px] text-ink-muted" data-id="element-9583" data-mp="125prn4qg6gwq">
                    {request.property} · {request.unit} · raised {request.raised} ·{' '}
                    {request.assignee}
                  </p>
                </div>
                <Badge tone={request.priority === 'Urgent' ? 'danger' : 'neutral'} data-id="element-9584" data-mp="e9tq7f606tq7">
                  {request.priority}
                </Badge>
                <Badge tone={request.status === 'Closed' ? 'ok' : 'brand'} data-id="element-9585" data-mp="j96msrmoff5z">{request.status}</Badge>
              </li>)}
          </ul>
        </Panel>}
    </div>;
}